// src/pages/Trend/components/dashboard/DashboardContent.js
import React from 'react';
import {
  TrendingUp,
  Building2,
  FlaskConical,
  Activity,
  Database,
  Zap,
  Package,
  Layers,
  BarChart3
} from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import './DashboardContent.css';

// Live Data Bar Component
const LiveDataIndicator = ({ translate }) => (
  <div className="live-data-section">
    <div className="live-data-container">
      {/* Gradient Top Stripe */}
      <div className="live-data-stripe"></div>

      {/* Content */}
      <div className="live-data-content">
        <div className="live-indicator">
          <span className="live-dot"></span>
          <span className="live-text">{translate('trendPage.dashboard.live.status') || 'LIVE'}</span>
        </div>

        <div className="data-stats">
          <div className="data-stat">
            <Database size={16} />
            <span className="stat-label">{translate('trendPage.dashboard.live.collecting') || 'Collecting'}</span>
            <span className="stat-value">2,847</span>
            <span className="stat-unit">{translate('trendPage.dashboard.live.productsPerDay') || 'products/day'}</span>
          </div>

          <div className="data-stat-divider"></div>

          <div className="data-stat">
            <Activity size={16} />
            <span className="stat-label">{translate('trendPage.dashboard.live.analyzing') || 'Analyzing'}</span>
            <span className="stat-value">18.4K</span>
            <span className="stat-unit">{translate('trendPage.dashboard.live.reviewsPerHour') || 'reviews/hour'}</span>
          </div>

          <div className="data-stat-divider"></div>

          <div className="data-stat">
            <Zap size={16} className="updating-icon" />
            <span className="stat-label">{translate('trendPage.dashboard.live.updating') || 'Updating'}</span>
            <span className="stat-value">
              <span className="pulse-dot"></span>
              {translate('trendPage.dashboard.live.realtime') || 'Real-time'}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Glass Card Component
const ScoreCard = ({ icon: Icon, score, label, narrative, color, colorBg }) => (
  <div
    className="score-card"
    style={{ '--card-accent': color }}
  >
    <div className="card-top">
      <div className="card-icon-wrapper" style={{ background: colorBg }}>
        <Icon size={20} strokeWidth={2} style={{ color }} />
      </div>
      <span className="card-label">{label}</span>
    </div>
    <div className="card-score" style={{ color }}>{score}</div>
    <div className="card-accent-bar" style={{ background: color }}></div>
    <p className="card-description">{narrative}</p>
  </div>
);

const DashboardContent = () => {
  const { translate } = useLanguage();

  const cards = [
    {
      icon: Building2,
      score: '557+',
      label: translate('trendPage.dashboard.cards.brands.label') || 'K-Beauty Brands',
      narrative: translate('trendPage.dashboard.cards.brands.narrative') || 'Discover leading brands, track market positions, and uncover competitive insights waiting to be explored.',
      color: '#06b6d4',
      colorBg: '#cffafe'
    },
    {
      icon: TrendingUp,
      score: '12.8M',
      label: translate('trendPage.dashboard.cards.reviews.label') || 'Consumer Reviews',
      narrative: translate('trendPage.dashboard.cards.reviews.narrative') || 'Millions of consumer voices analyzed. See what drives satisfaction, sentiment trends, and market reactions.',
      color: '#f59e0b',
      colorBg: '#fef3c7'
    },
    {
      icon: FlaskConical,
      score: '1,700+',
      label: translate('trendPage.dashboard.cards.ingredients.label') || 'Ingredients Tracked',
      narrative: translate('trendPage.dashboard.cards.ingredients.narrative') || 'From trending actives to emerging formulations. Explore ingredient patterns shaping the industry.',
      color: '#f97316',
      colorBg: '#ffedd5'
    },
    {
      icon: Package,
      score: '10K+',
      label: translate('trendPage.dashboard.cards.products.label') || 'Products Analyzed',
      narrative: translate('trendPage.dashboard.cards.products.narrative') || 'Deep analysis of products across all categories. Compare performance, pricing, and market positioning.',
      color: '#10b981',
      colorBg: '#d1fae5'
    },
    {
      icon: Layers,
      score: '5',
      label: translate('trendPage.dashboard.cards.categories.label') || 'Beauty Categories',
      narrative: translate('trendPage.dashboard.cards.categories.narrative') || 'Skincare, body care, hair care, sun care, and cleansing. Comprehensive coverage of the K-Beauty landscape.',
      color: '#a855f7',
      colorBg: '#f3e8ff'
    },
    {
      icon: BarChart3,
      score: '250+',
      label: translate('trendPage.dashboard.cards.concepts.label') || 'Trend Concepts',
      narrative: translate('trendPage.dashboard.cards.concepts.narrative') || 'From glass skin to skinimalism. Track emerging beauty concepts shaping consumer preferences.',
      color: '#ec4899',
      colorBg: '#fce7f3'
    }
  ];

  return (
    <div className="dashboard-content">
      <div className="dashboard-hero">
        <h1 className="dashboard-title">
          {translate('trendPage.dashboard.hero.title') || 'Your K-Beauty Intelligence Hub'}
        </h1>
        <p className="dashboard-subtitle">
          {translate('trendPage.dashboard.hero.subtitle') || 'Select Brand or Analysis tab to start exploring insights'}
        </p>
      </div>

      <div className="dashboard-score-cards">
        {cards.map((card, index) => (
          <ScoreCard key={index} {...card} />
        ))}
      </div>

      {/* Live Data Bar */}
      <LiveDataIndicator translate={translate} />
    </div>
  );
};

export default DashboardContent;
